import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router";
import { Building2, Car, LayoutGrid, List, MapPin, Users } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { LOCATION_SECTORS } from "./LocationInfo";
import { CallLink } from "../calls/CallTracker";

// LDF: Übersicht aller Locations – verbindet Firmen, Events und Aufträge über den Veranstaltungsort.

type LocationRow = {
  id: number;
  name: string;
  sector: string | null;
  district: string | null;
  city: string | null;
  address: string | null;
  capacity: string | null;
  phone_number: string | null;
  access_info: string | null;
  parking_info: string | null;
  setup_rules: string | null;
  commission_percent: number | null;
  logo: { src?: string } | null;
  hidden: boolean | null;
};

const TYPES: Record<string, string> = {
  "location-hotel": "Hotel",
  "location-restaurant": "Restaurant / Bar",
  eventlocation: "Eventlocation",
  "location-betreiber": "Betreiber",
};

export const LocationsPage = () => {
  const [type, setType] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<"name" | "aktivitaet">("aktivitaet");
  const [onlyOurs, setOnlyOurs] = useState(false);
  const navigate = useNavigate();
  const [view, setView] = useState<"kacheln" | "liste">(() => {
    try {
      return (localStorage.getItem("ldf.locations.view") as "kacheln" | "liste") ?? "kacheln";
    } catch {
      return "kacheln";
    }
  });
  // Ansicht per URL steuerbar (z. B. vom Claude-Chat): ?nurMitUns=1&typ=location-hotel&suche=…&ansicht=liste&sortierung=name
  const [params] = useSearchParams();
  useEffect(() => {
    // Nur bei gezielter Ansichtssteuerung übernehmen (Link oder Claude); fehlende Parameter = Standard
    if (![...params.keys()].some((k) => ["_ansicht", "nurMitUns", "typ", "suche", "ansicht", "sortierung"].includes(k))) return;
    setOnlyOurs(params.get("nurMitUns") === "1");
    setType(params.get("typ") ?? "");
    setSearch(params.get("suche") ?? "");
    const v = params.get("ansicht");
    if (v === "liste" || v === "kacheln") setView(v);
    const s = params.get("sortierung");
    if (s === "name" || s === "aktivitaet") setSort(s);
  }, [params]);

  useEffect(() => {
    try {
      localStorage.setItem("ldf.locations.view", view);
    } catch {
      // Speicher nicht verfügbar
    }
  }, [view]);

  const { data, isPending } = useQuery({
    queryKey: ["locations-page"],
    queryFn: async () => {
      const supabase = getSupabaseClient();
      const [companies, events, deals, extraVenues] = await Promise.all([
        supabase
          .from("companies")
          .select("id,name,sector,district,city,address,capacity,phone_number,access_info,parking_info,setup_rules,commission_percent,logo,hidden")
          .or(`sector.in.(${LOCATION_SECTORS.join(",")}),lead_segment.eq."Location – Empfehlungspartner"`)
          .eq("hidden", false)
          .order("name"),
        supabase.from("market_events").select("location_company_id").not("location_company_id", "is", null),
        supabase.from("deals").select("location_company_id").not("location_company_id", "is", null),
        supabase.from("deal_participants").select("company_id").eq("role", "weiterer-einsatzort"),
      ]);
      const count = (rows: { location_company_id: number }[] | null) =>
        (rows ?? []).reduce((m, r) => m.set(r.location_company_id, (m.get(r.location_company_id) ?? 0) + 1), new Map<number, number>());
      return {
        locations: (companies.data ?? []) as LocationRow[],
        events: count(events.data as { location_company_id: number }[] | null),
        // Aufträge am Hauptort plus weitere Einsatzorte (z. B. Installation in einer Bar)
        deals: count([
          ...((deals.data ?? []) as { location_company_id: number }[]),
          ...((extraVenues.data ?? []) as { company_id: number }[]).map((p) => ({ location_company_id: p.company_id })),
        ]),
      };
    },
  });

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (data?.locations ?? [])
      .filter((l) => (!onlyOurs || (data?.deals.get(l.id) ?? 0) > 0) && (!type || l.sector === type) && (!term || [l.name, l.district, l.address].some((v) => v?.toLowerCase().includes(term))))
      .sort((a, b) =>
        sort === "name"
          ? a.name.localeCompare(b.name)
          : (data!.deals.get(b.id) ?? 0) * 10 + (data!.events.get(b.id) ?? 0) - ((data!.deals.get(a.id) ?? 0) * 10 + (data!.events.get(a.id) ?? 0)) ||
            a.name.localeCompare(b.name),
      );
  }, [data, type, search, sort, onlyOurs]);

  return (
    <div className="mt-4 max-w-[1800px] mx-auto">
      <Card>
        <CardContent className="space-y-4">
          <div>
            <div className="flex flex-wrap items-center justify-between gap-2">
            <h1 className="font-display text-2xl font-semibold flex items-center gap-2">
              <Building2 className="h-5 w-5" /> Locations
            </h1>
            {/* Ansicht umschalten – gut sichtbar oben rechts, wird gemerkt */}
            <div className="flex gap-1 rounded-full bg-muted p-1" role="group" aria-label="Ansicht">
              {(
                [
                  ["kacheln", "Kacheln", LayoutGrid],
                  ["liste", "Liste", List],
                ] as const
              ).map(([value, label, Icon]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setView(value)}
                  className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm cursor-pointer ${view === value ? "bg-background shadow-sm font-medium" : "text-muted-foreground"}`}
                >
                  <Icon className="h-3.5 w-3.5" /> {label}
                </button>
              ))}
            </div>
            </div>
            <p className="text-sm text-muted-foreground">
              Wo die Events stattfinden: Hotels, Restaurants und Eventlocations mit Anfahrt, Parken und Aufbau-Regeln, dazu unsere
              Aufträge und die Events aus dem Kalender. Blauer Name = dort haben wir schon gearbeitet. Neue Location: Website-Link in der Firmenliste mit Cmd+V einfügen und Typ auf
              „Location“ stellen.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex gap-1 rounded-full bg-muted p-1">
              {[["", "Alle"], ...Object.entries(TYPES)].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setType(value)}
                  className={`rounded-full px-3 py-1 text-sm cursor-pointer ${type === value ? "bg-background shadow-sm font-medium" : "text-muted-foreground"}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setOnlyOurs(!onlyOurs)}
              className={`h-9 rounded-full px-3 text-sm cursor-pointer ${onlyOurs ? "bg-blue-600 text-white" : "border border-border text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950"}`}
            >
              Nur mit uns gearbeitet
            </button>
            <select value={sort} onChange={(e) => setSort(e.target.value as "name" | "aktivitaet")} className="h-9 rounded-md border border-input bg-background px-2 text-sm">
              <option value="aktivitaet">Sortierung: Aufträge & Events</option>
              <option value="name">Sortierung: Name</option>
            </select>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Suchen: Name, Bezirk, Adresse …"
              className="h-9 min-w-48 flex-1 rounded-md border border-input bg-background px-3 text-sm"
            />
          </div>

          {isPending ? (
            <p className="py-6 text-sm text-muted-foreground">Wird geladen …</p>
          ) : (
            <>
              <p className="text-xs text-muted-foreground">{rows.length} Locations</p>
              {view === "liste" ? (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[760px] text-sm">
                    <thead>
                      <tr className="border-b border-border text-left text-xs text-muted-foreground">
                        <th className="py-2 pl-2 pr-3 font-medium">Name</th>
                        <th className="py-2 pr-3 font-medium">Typ</th>
                        <th className="py-2 pr-3 font-medium">Adresse / Bezirk</th>
                        <th className="py-2 pr-3 font-medium">Kapazität</th>
                        <th className="py-2 pr-3 font-medium">Telefon</th>
                        <th className="py-2 pr-3 text-right font-medium">Aufträge</th>
                        <th className="py-2 pr-3 text-right font-medium">Events</th>
                        <th className="py-2 pr-3 font-medium">Logistik</th>
                        <th className="py-2 text-right font-medium">Provision</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((l) => {
                        const deals = data?.deals.get(l.id) ?? 0;
                        const events = data?.events.get(l.id) ?? 0;
                        const logistics = [l.access_info, l.parking_info, l.setup_rules].filter(Boolean).length;
                        return (
                          <tr
                            key={l.id}
                            onClick={() => navigate(`/companies/${l.id}/show/location`)}
                            className="cursor-pointer border-b border-border hover:bg-muted"
                          >
                            <td className={`py-2 pl-2 pr-3 font-medium ${deals > 0 ? "text-blue-600 dark:text-blue-400" : ""}`}>{l.name}</td>
                            <td className="py-2 pr-3 text-muted-foreground">{TYPES[l.sector ?? ""] ?? "Location"}</td>
                            <td className="max-w-56 truncate py-2 pr-3 text-muted-foreground" title={[l.address, l.district].filter(Boolean).join(" · ")}>
                              {[l.address, l.district].filter(Boolean).join(" · ")}
                            </td>
                            <td className="max-w-40 truncate py-2 pr-3 text-muted-foreground" title={l.capacity ?? ""}>
                              {l.capacity}
                            </td>
                            <td className="whitespace-nowrap py-2 pr-3">
                              {l.phone_number && (
                                <CallLink number={l.phone_number} name={l.name} companyId={l.id} />
                              )}
                            </td>
                            <td className="py-2 pr-3 text-right">{deals || ""}</td>
                            <td className="py-2 pr-3 text-right">{events || ""}</td>
                            <td className={`whitespace-nowrap py-2 pr-3 ${logistics ? "" : "text-muted-foreground"}`}>
                              {logistics ? `${logistics}/3` : "fehlt"}
                            </td>
                            <td className="py-2 text-right">{l.commission_percent != null ? `${l.commission_percent} %` : ""}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {rows.map((l) => {
                  const deals = data?.deals.get(l.id) ?? 0;
                  const events = data?.events.get(l.id) ?? 0;
                  const logistics = [l.access_info, l.parking_info, l.setup_rules].filter(Boolean).length;
                  return (
                    <Link key={l.id} to={`/companies/${l.id}/show/location`} className="no-underline">
                      <div
                        className="h-full rounded-lg border border-border p-3 hover:bg-muted"
                      >
                        <div className="flex items-start gap-2">
                          {l.logo?.src ? (
                            <img src={l.logo.src} alt="" className="h-9 w-9 shrink-0 rounded object-contain bg-background" />
                          ) : (
                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded bg-muted">
                              <Building2 className="h-4 w-4 text-muted-foreground" />
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className={`text-sm font-medium line-clamp-2 ${deals > 0 ? "text-blue-600 dark:text-blue-400" : ""}`}>{l.name}</div>
                            <div className="text-xs text-muted-foreground">{TYPES[l.sector ?? ""] ?? "Location"}</div>
                          </div>
                        </div>
                        <div className="mt-2 space-y-0.5 text-xs text-muted-foreground">
                          {(l.district || l.address) && (
                            <div className="flex items-center gap-1 truncate">
                              <MapPin className="h-3 w-3 shrink-0" />
                              <span className="truncate">{[l.address, l.district].filter(Boolean).join(" · ")}</span>
                            </div>
                          )}
                          {l.capacity && (
                            <div className="flex items-center gap-1 truncate">
                              <Users className="h-3 w-3 shrink-0" />
                              <span className="truncate">{l.capacity}</span>
                            </div>
                          )}
                        </div>
                        <div className="mt-2 flex flex-wrap gap-1 text-[11px]">
                          {deals > 0 && (
                            <span className="rounded-full bg-blue-600 px-2 py-0.5 text-white">
                              {deals} Auftr{deals === 1 ? "ag" : "äge"}
                            </span>
                          )}
                          {events > 0 && <span className="rounded-full bg-secondary px-2 py-0.5 text-secondary-foreground">{events} Events</span>}
                          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${logistics ? "bg-muted" : "border border-dashed border-border text-muted-foreground"}`}>
                            <Car className="h-3 w-3" /> {logistics ? `${logistics}/3 Logistik` : "Logistik fehlt"}
                          </span>
                          {l.commission_percent != null && <span className="rounded-full bg-muted px-2 py-0.5">{l.commission_percent} % Provision</span>}
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

LocationsPage.path = "/locations";
